import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import type { Permission } from '@sebastian/shared';
import type { ReactNode } from 'react';
import { AppShell } from './components/AppShell';
import { CurrencyProvider } from './lib/currency';
import { useAuth } from './lib/auth';
import { useSettings } from './hooks/queries';
import { EmptyState, Spinner } from './components/ui';
import { LoginPage } from './pages/LoginPage';
import { PosPage } from './pages/PosPage';
import { OrderPage } from './pages/OrderPage';
import { StationPage } from './pages/StationPage';
import { PaymentsPage } from './pages/PaymentsPage';
import { PrintStationPage } from './pages/PrintStationPage';
import { OrderHistoryPage } from './pages/OrderHistoryPage';
import { BookingsPage } from './pages/BookingsPage';
import { ReportsPage } from './pages/ReportsPage';
import { GiveawaysPage } from './pages/GiveawaysPage';
import { MenuAdminPage } from './pages/MenuAdminPage';
import { StockPage } from './pages/StockPage';
import { CategoriesAdminPage } from './pages/CategoriesAdminPage';
import { ModifiersAdminPage } from './pages/ModifiersAdminPage';
import { TablesAdminPage } from './pages/TablesAdminPage';
import { UsersAdminPage } from './pages/UsersAdminPage';
import { SettingsPage } from './pages/SettingsPage';

/** Blocks a route until the session is known, then until the role permits it. */
function Protected({ permission, children }: { permission?: Permission; children: ReactNode }) {
  const { status, allows, home } = useAuth();
  const location = useLocation();

  if (status === 'loading') return <Spinner label="Restoring session…" />;
  if (status === 'anonymous') return <Navigate to="/login" replace state={{ from: location }} />;

  if (permission && !allows(permission)) {
    return (
      <div className="p-8">
        <EmptyState
          title="Not available for your role"
          message="Ask a manager if you need access to this screen."
          action={
            <a className="text-sm text-accent underline underline-offset-4" href={home}>
              Back to your home screen
            </a>
          }
        />
      </div>
    );
  }

  return <>{children}</>;
}

function Shell({ permission, children }: { permission?: Permission; children: ReactNode }) {
  return (
    <Protected permission={permission}>
      <AppShell>{children}</AppShell>
    </Protected>
  );
}

export function App() {
  const { status, home } = useAuth();
  const { data: settings } = useSettings({ enabled: status === 'authenticated' });

  return (
    <CurrencyProvider settings={settings}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />

        <Route path="/" element={<Navigate to={status === 'authenticated' ? home : '/login'} replace />} />

        <Route path="/pos" element={<Shell permission="pos:operate"><PosPage /></Shell>} />
        <Route path="/order/:tableId" element={<Protected permission="pos:operate"><OrderPage /></Protected>} />

        <Route
          path="/kitchen"
          element={<Shell permission="kds:kitchen"><StationPage destination="KITCHEN" /></Shell>}
        />
        <Route path="/bar" element={<Shell permission="kds:bar"><StationPage destination="BAR" /></Shell>} />

        <Route path="/payments" element={<Shell permission="payments:process"><PaymentsPage /></Shell>} />
        {/* Deliberately outside the shell: this screen is run full-window on the
            till, often as a Chrome app window with no navigation around it. */}
        <Route
          path="/print-station"
          element={<Protected permission="print:station"><PrintStationPage /></Protected>}
        />
        <Route path="/orders" element={<Shell permission="orders:history"><OrderHistoryPage /></Shell>} />
        <Route path="/bookings" element={<Shell permission="pos:operate"><BookingsPage /></Shell>} />
        {/* Management only: `reports:read` is held by ADMIN and MANAGER alone. */}
        <Route path="/reports" element={<Shell permission="reports:read"><ReportsPage /></Shell>} />
        <Route path="/giveaways" element={<Shell permission="reports:read"><GiveawaysPage /></Shell>} />

        {/* Set daily before service; `admin:menu` is held by ADMIN and MANAGER alone. */}
        <Route path="/stock" element={<Shell permission="admin:menu"><StockPage /></Shell>} />
        <Route path="/menu" element={<Shell permission="admin:menu"><MenuAdminPage /></Shell>} />
        <Route path="/categories" element={<Shell permission="admin:menu"><CategoriesAdminPage /></Shell>} />
        <Route path="/modifiers" element={<Shell permission="admin:menu"><ModifiersAdminPage /></Shell>} />
        <Route path="/tables" element={<Shell permission="admin:tables"><TablesAdminPage /></Shell>} />
        <Route path="/users" element={<Shell permission="admin:users"><UsersAdminPage /></Shell>} />
        <Route path="/settings" element={<Shell permission="admin:settings"><SettingsPage /></Shell>} />

        <Route
          path="*"
          element={
            <Shell>
              <div className="p-8">
                <EmptyState title="Page not found" message="That screen does not exist." />
              </div>
            </Shell>
          }
        />
      </Routes>
    </CurrencyProvider>
  );
}
